import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribes a component to a CSS media query.
 *
 * `useSyncExternalStore` rather than `useState` + an effect: the matcher is
 * external state that can already be true on the very first paint, and the
 * effect version renders `false` once before correcting itself — which is a
 * visible flash of the wrong layout on every load, not a subtle one when the
 * thing being switched is the whole navigation.
 *
 * The same shape TopNav already uses for `prefers-color-scheme`.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => {
        list.removeEventListener('change', onChange)
      }
    },
    [query],
  )

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])

  // Server/JSDOM have no matchMedia. Reporting "not matching" makes the mobile
  // branch the one that renders, which is the safe default: it is the layout
  // that fits inside every viewport.
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/** The shell's rail breakpoint — `lg`, the same 1024px the grid switches on. */
export const useIsDesktop = () => useMediaQuery('(width >= 64rem)')
